import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import PreferenceTable from '../components/PreferenceTable';
import { useTablePreferences } from '../hooks/useTablePreferences';
import FileUpload from '../components/FileUpload';
import SortableServiceLines from '../components/SortableServiceLines';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search,
  Target,
  Building,
  User,
  MapPin,
  ArrowRight,
  Trash2,
  Edit,
  Loader2,
  DollarSign,
  Paperclip,
  X,
  GripVertical,
  LayoutGrid,
  TableIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate } from '../lib/utils';
import MapButtons from '../components/MapButtons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPPORTUNITY_STATUSES = [
  { value: 'New Lead', label: 'New Lead', color: 'bg-slate-100 text-slate-800' },
  { value: 'Qualified', label: 'Qualified', color: 'bg-blue-100 text-blue-800' },
  { value: 'Site Visit Scheduled', label: 'Site Visit', color: 'bg-purple-100 text-purple-800' },
  { value: 'Ready for Proposal', label: 'Ready for Proposal', color: 'bg-amber-100 text-amber-800' },
  { value: 'Converted', label: 'Converted', color: 'bg-green-100 text-green-800' },
  { value: 'Lost', label: 'Lost', color: 'bg-red-100 text-red-800' },
  // "Disqualified" is a distinct terminal state from "Lost": Lost = we
  // pursued and didn't win; Disqualified = not a fit (out of service
  // area, wrong service, spam, dupe, etc.) so the office can drop the
  // lead without polluting the win/loss ratio. Zinc tone keeps it
  // visually neutral — clearly closed, but not urgent-red like Lost.
  { value: 'Disqualified', label: 'Disqualified', color: 'bg-zinc-200 text-zinc-700' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const emptyForm = {
  client_id: '',
  contact_id: '',  // Now refers to person_id from People collection
  contact_first_name: '',
  contact_last_name: '',
  contact_name: '',  // Keep for backward compatibility (combined name)
  contact_phone: '',
  contact_email: '',
  title: '',
  description: '',
  status: 'New Lead',
  service_lines: [],  // [{service_type, description, quantity, unit_price, total}]
  site_address_street: '',
  site_address_city: '',
  site_address_state: '',
  site_address_zip: '',
  site_plan_notes: '',
  expected_close_date: '',
  notes: ''
};

const emptyServiceLine = {
  service_type: '',
  description: '',
  quantity: 1,
  unit_price: 0,
  total: 0
};

export default function Opportunities() {
  const { user, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Permission checks - Opportunities requires same permission as proposals
  const canViewOpportunities = isAdmin() || hasPermission('view_proposals') || hasPermission('create_proposals');
  
  // Redirect unauthorized users
  useEffect(() => {
    if (!canViewOpportunities) {
      toast.error('Access denied. You do not have permission to view opportunities.');
      navigate('/');
    }
  }, [canViewOpportunities, navigate]);
  
  const [opportunities, setOpportunities] = useState([]);
  const [clients, setClients] = useState([]);
  const [people, setPeople] = useState([]);  // New People collection
  const [loadingPeople, setLoadingPeople] = useState(true);  // Track people loading
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedClientContacts, setSelectedClientContacts] = useState([]);
  
  // View mode: 'table' or 'cards' - default to table, persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('opportunitiesViewMode') || 'table';
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('opportunitiesViewMode', viewMode);
  }, [viewMode]);
  
  // Handle ?new=true URL param to open create dialog
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      // Open the create dialog
      setSelectedOpp(null);
      setFormData(emptyForm);
      setShowDialog(true);
      // Clear the param from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Contact search state
  const [contactSearch, setContactSearch] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [filteredPeople, setFilteredPeople] = useState([]);
  
  // Inline client creation state
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [newClientData, setNewClientData] = useState({
    client_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    physical_street: '',
    physical_city: '',
    physical_state: '',
    physical_zip: ''
  });
  
  // Inline contact creation state
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    company_id: '',
    company_name: '',
    job_title: '',
    phone: '',
    email: ''
  });

  // Service quantity dialog state (for services that require quantity input like CCTV, Soft Dig)
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [quantityDialogData, setQuantityDialogData] = useState({
    serviceIndex: null,
    serviceName: '',
    quantityLabel: '',
    quantityPrompt: '',
    quantity: 1
  });

  const fetchOpportunities = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await axios.get(`${API}/opportunities?${params}`);
      setOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchPeople = async () => {
    setLoadingPeople(true);
    try {
      const response = await axios.get(`${API}/people`);
      setPeople(response.data);
      // Initialize filteredPeople when people loads
      setFilteredPeople(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch people:', error);
    } finally {
      setLoadingPeople(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  useEffect(() => {
    fetchOpportunities();
    fetchClients();
    fetchPeople();
    fetchServices();
  }, [fetchOpportunities]);

  // Filter people based on search
  useEffect(() => {
    if (contactSearch.trim()) {
      const searchLower = contactSearch.toLowerCase();
      const filtered = people.filter(p => 
        p.full_name?.toLowerCase().includes(searchLower) ||
        p.company_name?.toLowerCase().includes(searchLower) ||
        p.primary_phone?.includes(contactSearch) ||
        p.primary_email?.toLowerCase().includes(searchLower)
      );
      setFilteredPeople(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setFilteredPeople(people.slice(0, 10));
    }
  }, [contactSearch, people]);

  // Handle selecting a person from the dropdown
  const handleSelectPerson = (person) => {
    setFormData(prev => ({
      ...prev,
      contact_id: person.id,
      contact_first_name: person.first_name || '',
      contact_last_name: person.last_name || '',
      contact_name: person.full_name,
      contact_phone: person.primary_phone || '',
      contact_email: person.primary_email || '',
      client_id: person.company_id || '',
    }));
    
    // If person has a company, update client name display
    if (person.company_id) {
      const company = clients.find(c => c.id === person.company_id);
      if (company) {
        // Also set company-related fields if needed
      }
    }
    
    setContactSearch(person.full_name);
    setShowContactDropdown(false);
  };

  // Create new contact inline
  const handleCreateContact = async () => {
    if (!newContactData.first_name.trim() || !newContactData.last_name.trim()) {
      toast.error('Please enter first and last name');
      return;
    }
    
    setSavingContact(true);
    try {
      let companyId = newContactData.company_id;
      let companyName = newContactData.company_name;
      
      // If creating a new company, create it first with contact info
      if (companyId === '__new__' && companyName?.trim()) {
        try {
          // Build company payload with contact info from the new contact
          const companyPayload = {
            company_name: companyName.trim(),
            client_name: companyName.trim(),
            contacts: [],
            // Include primary contact info in company record
            email: newContactData.email || null,
            phone1: newContactData.phone || null,
            // Set primary contact name
            contact_name: `${newContactData.first_name} ${newContactData.last_name}`.trim(),
            // If we have address info from the contact, add it as billing address
            billing_street: newContactData.street || null,
            billing_city: newContactData.city || null,
            billing_state: newContactData.state || null,
            billing_zip: newContactData.zip || null
          };
          
          const companyResponse = await axios.post(`${API}/contacts`, companyPayload);
          companyId = companyResponse.data.id;
          // Refresh clients list
          await fetchClients();
          toast.success(`Company "${companyName}" created!`);
        } catch (companyError) {
          console.error('Failed to create company:', companyError);
          toast.error('Failed to create company');
          setSavingContact(false);
          return;
        }
      } else if (companyId === '__new__') {
        // User selected "Create New Company" but didn't enter a name
        companyId = null;
        companyName = null;
      } else if (companyId) {
        // Existing company selected - update company with contact info if company is missing contact details
        try {
          // Fetch current company to check if it needs updating
          const companyResponse = await axios.get(`${API}/contacts/${companyId}`);
          const existingCompany = companyResponse.data;
          
          // Only update if company is missing primary contact info
          const needsUpdate = !existingCompany.phone1 || !existingCompany.email || !existingCompany.contact_name;
          
          if (needsUpdate) {
            const updatePayload = {};
            if (!existingCompany.contact_name) {
              updatePayload.contact_name = `${newContactData.first_name} ${newContactData.last_name}`.trim();
            }
            if (!existingCompany.phone1 && newContactData.phone) {
              updatePayload.phone1 = newContactData.phone;
            }
            if (!existingCompany.email && newContactData.email) {
              updatePayload.email = newContactData.email;
            }
            
            if (Object.keys(updatePayload).length > 0) {
              await axios.put(`${API}/contacts/${companyId}`, updatePayload);
              await fetchClients(); // Refresh clients list
              console.log('Updated company with contact info:', updatePayload);
            }
          }
          
          // Get company name for the contact record
          companyName = existingCompany.company_name || existingCompany.client_name;
        } catch (companyError) {
          console.error('Failed to update company with contact info:', companyError);
          // Continue with contact creation even if company update fails
        }
      }
      
      const contactPayload = {
        first_name: newContactData.first_name.trim(),
        last_name: newContactData.last_name.trim(),
        company_id: companyId || null,
        company_name: companyName || null,
        job_title: newContactData.job_title || null,
        phones: newContactData.phone ? [{
          number: newContactData.phone,
          type: 'mobile',
          is_primary: true
        }] : [],
        emails: newContactData.email ? [{
          email: newContactData.email,
          type: 'work',
          is_primary: true
        }] : [],
        addresses: [],
        preferred_contact_method: 'phone'
      };
      
      const response = await axios.post(`${API}/people`, contactPayload);
      const newPerson = response.data;
      
      // Refresh people list
      await fetchPeople();
      
      // Auto-select the new contact
      setFormData(prev => ({
        ...prev,
        contact_id: newPerson.id,
        contact_name: newPerson.full_name,
        contact_phone: newPerson.primary_phone || newContactData.phone,
        contact_email: newPerson.primary_email || newContactData.email,
        client_id: companyId || ''
      }));
      
      setContactSearch(newPerson.full_name);
      
      // Reset and close the inline form
      setNewContactData({
        first_name: '',
        last_name: '',
        company_id: '',
        company_name: '',
        job_title: '',
        phone: '',
        email: ''
      });
      setShowNewContactForm(false);
      
      toast.success(`Contact "${newPerson.full_name}" created and selected!`);
    } catch (error) {
      console.error('Failed to create contact:', error);
      toast.error('Failed to create contact');
    } finally {
      setSavingContact(false);
    }
  };

  useEffect(() => {
    if (formData.client_id) {
      const client = clients.find(c => c.id === formData.client_id);
      if (client) {
        const contacts = client.contacts || client.people || [];
        setSelectedClientContacts(contacts.map(c => ({
          id: c.id,
          name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()
        })));
      } else {
        setSelectedClientContacts([]);
      }
    } else {
      setSelectedClientContacts([]);
    }
  }, [formData.client_id, clients]);

  // Create new client inline
  const handleCreateClient = async () => {
    if (!newClientData.client_name.trim()) {
      toast.error('Please enter a client/company name');
      return;
    }
    
    setSavingClient(true);
    try {
      const clientPayload = {
        client_name: newClientData.client_name.trim(),
        company_name: newClientData.client_name.trim(),
        physical_street: newClientData.physical_street,
        physical_city: newClientData.physical_city,
        physical_state: newClientData.physical_state,
        physical_zip: newClientData.physical_zip,
        contacts: newClientData.contact_name ? [{
          name: newClientData.contact_name,
          email: newClientData.contact_email,
          phone: newClientData.contact_phone,
          title: ''
        }] : []
      };
      
      const response = await axios.post(`${API}/contacts`, clientPayload);
      const newClient = response.data;
      
      // Refresh clients list and select the new client
      await fetchClients();
      
      // Auto-fill the opportunity form with the new client's data
      setFormData(prev => ({
        ...prev,
        client_id: newClient.id,
        contact_id: newClient.contacts?.[0]?.id || '',
        site_address_street: newClientData.physical_street,
        site_address_city: newClientData.physical_city,
        site_address_state: newClientData.physical_state,
        site_address_zip: newClientData.physical_zip
      }));
      
      // Reset and close the inline form
      setNewClientData({
        client_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        physical_street: '',
        physical_city: '',
        physical_state: '',
        physical_zip: ''
      });
      setShowNewClientForm(false);
      
      toast.success(`Client "${newClient.client_name || newClient.company_name}" created and selected!`);
    } catch (error) {
      console.error('Failed to create client:', error);
      toast.error('Failed to create client');
    } finally {
      setSavingClient(false);
    }
  };

  const handleAddNew = () => {
    setSelectedOpp(null);
    setFormData(emptyForm);
    setContactSearch('');
    setShowNewClientForm(false);
    setShowNewContactForm(false);
    setShowDialog(true);
  };

  const handleEdit = (opp) => {
    setSelectedOpp(opp);
    setFormData({
      client_id: opp.client_id || '',
      contact_id: opp.contact_id || '',
      contact_name: opp.contact_name || '',
      // Preserve linked-contact first/last so the dialog's First Name /
      // Last Name inputs prefill correctly. Without these two lines the
      // controlled inputs render blank even when opp.contact_name is
      // set (testing-agent iter-96 caught this after row-click was
      // rewired to open the Edit dialog directly).
      contact_first_name: opp.contact_first_name || '',
      contact_last_name: opp.contact_last_name || '',
      contact_phone: opp.contact_phone || '',
      contact_email: opp.contact_email || '',
      title: opp.title || '',
      description: opp.description || '',
      status: opp.status || 'Lead',
      service_lines: opp.service_lines || [],
      site_address_street: opp.site_address_street || '',
      site_address_city: opp.site_address_city || '',
      site_address_state: opp.site_address_state || '',
      site_address_zip: opp.site_address_zip || '',
      site_plan_notes: opp.site_plan_notes || '',
      expected_close_date: opp.expected_close_date || '',
      notes: opp.notes || ''
    });
    setContactSearch(opp.contact_name || '');
    setShowDialog(true);
  };

  const handleSave = async (keepDialogOpen = false) => {
    if (!formData.title) {
      toast.error('Please enter a Job Title');
      return;
    }

    // Calculate estimated total from service lines
    const estimatedTotal = (formData.service_lines || []).reduce((sum, line) => sum + (line.total || 0), 0);

    // Get client name from selected client or contact's company
    let clientName = '';
    if (formData.client_id) {
      const client = clients.find(c => c.id === formData.client_id);
      clientName = client?.company_name || client?.client_name || '';
    }

    const payload = {
      ...formData,
      client_name: clientName,
      estimated_total: estimatedTotal,
      contact_id: formData.contact_id || null,
      contact_name: formData.contact_name || null,
      contact_phone: formData.contact_phone || null,
      contact_email: formData.contact_email || null
    };

    setSaving(true);
    try {
      let savedOpp;
      if (selectedOpp) {
        const response = await axios.put(`${API}/opportunities/${selectedOpp.id}`, payload);
        savedOpp = response.data;
        toast.success('Opportunity updated');
      } else {
        const response = await axios.post(`${API}/opportunities`, payload);
        savedOpp = response.data;
        toast.success('Opportunity created - you can now add files');
      }
      
      if (keepDialogOpen && savedOpp) {
        // Keep dialog open and update selectedOpp so FileUpload works
        setSelectedOpp(savedOpp);
        setFormData(prev => ({ ...prev, ...savedOpp }));
        fetchOpportunities();
      } else {
        setShowDialog(false);
        fetchOpportunities();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save opportunity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/opportunities/${selectedOpp.id}`);
      toast.success('Opportunity deleted');
      setShowDeleteDialog(false);
      setShowDetailDialog(false);
      setSelectedOpp(null);
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to delete opportunity');
    } finally {
      setSaving(false);
    }
  };

  const refreshSelectedOpp = async () => {
    if (!selectedOpp) return;
    try {
      const response = await axios.get(`${API}/opportunities/${selectedOpp.id}`);
      setSelectedOpp(response.data);
      fetchOpportunities();
    } catch (error) {
      console.error('Failed to refresh opportunity:', error);
    }
  };

  const handleViewDetail = async (opp) => {
    try {
      const response = await axios.get(`${API}/opportunities/${opp.id}`);
      setSelectedOpp(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load opportunity details');
    }
  };

  const handleConvertToProposal = async (opp) => {
    if (opp.status === 'Converted') {
      toast.error('This opportunity has already been converted');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/opportunities/${opp.id}/convert-to-proposal`);
      toast.success(`Proposal ${response.data.proposal_number} created!`);
      setShowDetailDialog(false);
      // Backend returns { proposal_id, proposal_number } — not { id, ... }
      const newProposalId = response.data.proposal_id || response.data.id;
      navigate(`/proposals/${newProposalId}/preview`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to proposal');
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = OPPORTUNITY_STATUSES.find(s => s.value === status) || OPPORTUNITY_STATUSES[0];
    return <Badge className={statusInfo.color} data-testid={`opportunity-status-badge-${status}`}>{statusInfo.label}</Badge>;
  };

  const filteredOpportunities = opportunities.filter(opp => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      opp.title?.toLowerCase().includes(searchLower) ||
      opp.opportunity_number?.toLowerCase().includes(searchLower) ||
      opp.client_name?.toLowerCase().includes(searchLower) ||
      opp.description?.toLowerCase().includes(searchLower) ||
      opp.notes?.toLowerCase().includes(searchLower) ||
      opp.project_address?.toLowerCase().includes(searchLower) ||
      opp.service_type?.toLowerCase().includes(searchLower) ||
      opp.source?.toLowerCase().includes(searchLower) ||
      opp.status?.toLowerCase().includes(searchLower) ||
      opp.priority?.toLowerCase().includes(searchLower) ||
      opp.created_at?.toLowerCase().includes(searchLower)
    );
  });

  // Sort function
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Sorted and filtered opportunities
  const sortedOpportunities = [...filteredOpportunities].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Handle dates
    if (sortBy === 'created_at' || sortBy === 'expected_close_date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    
    // Handle numbers
    if (sortBy === 'value') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }
    
    // Handle strings
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Column config for PreferenceTable — mirrors the previous hard-coded table.
  const opportunityColumns = [
    { id: 'opportunity_number', label: 'Opp #', sortKey: 'opportunity_number', defaultWidth: 130,
      render: (opp) => <span className="font-mono text-amber-600 font-bold text-sm">{opp.opportunity_number}</span> },
    { id: 'job_title', label: 'Job Title', sortKey: 'job_title', defaultWidth: 220,
      cellClassName: 'font-medium',
      render: (opp) => <span className="truncate block">{opp.job_title || opp.title || '—'}</span> },
    { id: 'client_name', label: 'Client', sortKey: 'client_name', defaultWidth: 180,
      render: (opp) => opp.client_name || '—' },
    { id: 'contact_name', label: 'Contact', sortKey: 'contact_name', defaultWidth: 170,
      render: (opp) => opp.contact_name || '—' },
    { id: 'estimated_total', label: 'Value', sortKey: 'estimated_total', defaultWidth: 130, alignRight: true,
      cellClassName: 'text-green-700 font-medium',
      render: (opp) => `$${(opp.estimated_total || opp.estimated_value || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` },
    { id: 'status', label: 'Status', sortKey: 'status', defaultWidth: 160,
      render: (opp) => getStatusBadge(opp.status) },
    { id: 'created_at', label: 'Created', sortKey: 'created_at', defaultWidth: 140,
      cellClassName: 'text-sm text-slate-500',
      render: (opp) => opp.created_at ? format(parseLocalDate(opp.created_at.split('T')[0]), 'MMM d, yyyy') : '—' },
    { id: 'actions', label: 'Actions', defaultWidth: 160, alignRight: true,
      render: (opp) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(opp)} data-testid={`edit-opp-${opp.id}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setSelectedOpp(opp); setShowDeleteDialog(true); }}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
          {opp.status !== 'Converted' && opp.status !== 'Lost' && opp.status !== 'Disqualified' && (
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              onClick={() => handleConvertToProposal(opp)}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) },
  ];
  const opportunitiesPrefs = useTablePreferences('opportunities', opportunityColumns);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-48 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Opportunities
          </h1>
          <p className="text-slate-500 mt-1">Manage sales pipeline and convert to proposals</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          <Button 
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
            onClick={handleAddNew}
            data-testid="add-opportunity-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Opportunity
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search opportunities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-50"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-slate-50 px-3 py-2 text-sm"
              data-testid="opportunity-status-filter"
            >
              <option value="">All Statuses</option>
              {OPPORTUNITY_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {OPPORTUNITY_STATUSES.map(status => {
          const count = opportunities.filter(o => o.status === status.value).length;
          return (
            <Card 
              key={status.value} 
              className={`border-slate-200 cursor-pointer hover:border-amber-500/50 ${statusFilter === status.value ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status.value ? '' : status.value)}
              data-testid={`opportunity-status-tile-${status.value}`}
            >
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-slate-900" data-testid={`opportunity-status-count-${status.value}`}>{count}</div>
                <div className="text-xs text-slate-500">{status.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Opportunities List */}
      {filteredOpportunities.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No opportunities found</h3>
            <p className="text-slate-500">
              {search || statusFilter ? 'Try adjusting your filters' : 'Create your first opportunity to get started'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View — resize, reorder, hide columns are per-user prefs */
        <Card className="border-slate-200">
          <div className="p-3">
            <PreferenceTable
              columns={opportunityColumns}
              rows={sortedOpportunities}
              rowKey={(o) => o.id}
              prefs={opportunitiesPrefs}
              sortBy={sortBy}
              sortDir={sortOrder}
              onSort={(col) => handleSort(col.sortKey)}
              onRowClick={(opp) => handleEdit(opp)}
              rowClassName={() => 'cursor-pointer hover:bg-slate-50'}
              emptyMessage="No opportunities match your filters."
              data-testid="opportunities-table"
            />
          </div>
        </Card>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedOpportunities.map(opp => (
            <Card 
              key={opp.id} 
              className="border-slate-200 hover:border-amber-500/50 transition-all cursor-pointer"
              onClick={() => handleEdit(opp)}
              data-testid={`opportunity-card-${opp.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs font-mono text-amber-600 font-bold">{opp.opportunity_number}</p>
                    <h3 className="font-semibold text-slate-900 mt-1">{opp.title}</h3>
                  </div>
                  {getStatusBadge(opp.status)}
                </div>

                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{opp.client_name || 'No client'}</span>
                  </div>
                  {opp.contact_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{opp.contact_name}</span>
                    </div>
                  )}
                  {opp.site_address_city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{opp.site_address_city}, {opp.site_address_state}</span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <MapButtons 
                          address={[opp.site_address_street, opp.site_address_city, opp.site_address_state, opp.site_address_zip].filter(Boolean).join(', ')}
                          showLabels={false}
                          variant="ghost"
                        />
                      </div>
                    </div>
                  )}
                  {(opp.estimated_total > 0 || opp.estimated_value > 0) && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-green-700 font-medium">${(opp.estimated_total || opp.estimated_value || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  )}
                  {opp.attachments?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-slate-400" />
                      <span>{opp.attachments.length} file{opp.attachments.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(opp); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedOpp(opp); setShowDeleteDialog(true); }}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  {opp.status !== 'Converted' && opp.status !== 'Lost' && opp.status !== 'Disqualified' && (
                    <Button 
                      size="sm" 
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                      onClick={(e) => { e.stopPropagation(); handleConvertToProposal(opp); }}
                    >
                      <ArrowRight className="w-4 h-4 mr-1" />
                      To Proposal
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog with File Upload */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedOpp && (
            <>
              <DialogHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono text-amber-600 font-bold">{selectedOpp.opportunity_number}</p>
                    <DialogTitle className="text-lg sm:text-xl font-bold mt-1 break-words">{selectedOpp.title}</DialogTitle>
                  </div>
                  <div className="shrink-0">
                    {getStatusBadge(selectedOpp.status)}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 sm:space-y-6 pt-4">
                {/* Client & Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <Building className="w-4 h-4 flex-shrink-0" />Client
                    </h4>
                    <p className="font-medium break-words">{selectedOpp.client_name || 'Not specified'}</p>
                    {selectedOpp.contact_name && (
                      <p className="text-sm text-slate-500 mt-1 break-words">Contact: {selectedOpp.contact_name}</p>
                    )}
                    {selectedOpp.contact_email && (
                      <p className="text-sm text-slate-500 break-all">
                        <a href={`mailto:${selectedOpp.contact_email}`} className="text-blue-600 hover:underline">
                          {selectedOpp.contact_email}
                        </a>
                      </p>
                    )}
                    {selectedOpp.contact_phone && (
                      <p className="text-sm text-slate-500">
                        <a href={`tel:${selectedOpp.contact_phone}`} className="text-blue-600 hover:underline">
                          {selectedOpp.contact_phone}
                        </a>
                      </p>
                    )}
                  </div>
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />Site Address
                    </h4>
                    <p className="text-sm break-words">
                      {selectedOpp.site_address_street && <>{selectedOpp.site_address_street}<br /></>}
                      {selectedOpp.site_address_city}, {selectedOpp.site_address_state} {selectedOpp.site_address_zip}
                    </p>
                    {(selectedOpp.site_address_street || selectedOpp.site_address_city) && (
                      <div className="mt-2">
                        <MapButtons 
                          address={[selectedOpp.site_address_street, selectedOpp.site_address_city, selectedOpp.site_address_state, selectedOpp.site_address_zip].filter(Boolean).join(', ')}
                          size="sm"
                          showLabels={false}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Lines & Pricing */}
                {selectedOpp.service_lines && selectedOpp.service_lines.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 sm:px-4 py-2 font-semibold text-sm">Service Line Items</div>
                    <div className="overflow-x-auto -mx-px">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-2 sm:px-4 py-2 text-left">Service</th>
                            <th className="px-2 sm:px-4 py-2 text-left hidden sm:table-cell">Description</th>
                            <th className="px-2 sm:px-4 py-2 text-center">Type</th>
                            <th className="px-2 sm:px-4 py-2 text-right">Qty</th>
                            <th className="px-2 sm:px-4 py-2 text-right">Price</th>
                            <th className="px-2 sm:px-4 py-2 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOpp.service_lines.map((line, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-2 sm:px-4 py-2 truncate max-w-[100px] sm:max-w-none">{line.service_type || '-'}</td>
                              <td className="px-2 sm:px-4 py-2 hidden sm:table-cell">{line.description || '-'}</td>
                              <td className="px-2 sm:px-4 py-2 text-center text-xs text-slate-500">{line.rate_type === 'hour' ? '/hr' : line.rate_type === 'day' ? '/day' : line.rate_type === 'night' ? '/night' : line.rate_type === 'hole' ? '/hole' : line.rate_type === 'linear_footage' ? '/LF' : 'fixed'}</td>
                              <td className="px-2 sm:px-4 py-2 text-right">{line.quantity || 1}</td>
                              <td className="px-2 sm:px-4 py-2 text-right">${(line.unit_price || 0).toFixed(2)}</td>
                              <td className="px-2 sm:px-4 py-2 text-right font-medium">${(line.total || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-green-50">
                          <tr className="border-t-2">
                            <td colSpan={3} className="px-2 sm:px-4 py-2 text-right font-semibold sm:hidden">Est. Total:</td>
                            <td colSpan={5} className="px-2 sm:px-4 py-2 text-right font-semibold hidden sm:table-cell">Estimated Total:</td>
                            <td className="px-2 sm:px-4 py-2 text-right font-bold text-green-700">
                              ${(selectedOpp.estimated_total || selectedOpp.service_lines.reduce((s, l) => s + (l.total || 0), 0)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                ) : (selectedOpp.estimated_value || selectedOpp.estimated_total) ? (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <span className="text-lg text-green-700 font-bold flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Estimated Value: ${(selectedOpp.estimated_total || selectedOpp.estimated_value || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                  </div>
                ) : null}

                {/* Site Plan Notes */}
                {selectedOpp.site_plan_notes && (
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-amber-700 mb-2">Site Plan / Request Notes</h4>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedOpp.site_plan_notes}</p>
                  </div>
                )}

                {/* Attachments Section - Using reusable FileUpload component */}
                <FileUpload
                  files={selectedOpp.attachments || []}
                  uploadEndpoint={`/api/opportunities/${selectedOpp.id}/files`}
                  deleteEndpoint={`/api/opportunities/${selectedOpp.id}/files`}
                  onUploadSuccess={refreshSelectedOpp}
                  onDeleteSuccess={refreshSelectedOpp}
                  title="Attachments"
                  emptyMessage="No files attached yet. Upload site plans, photos, documents."
                />

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setShowDetailDialog(false); handleEdit(selectedOpp); }}>
                      <Edit className="w-4 h-4 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => { setShowDeleteDialog(true); }}>
                      <Trash2 className="w-4 h-4 mr-1" />Delete
                    </Button>
                  </div>
                  {selectedOpp.status !== 'Converted' && selectedOpp.status !== 'Lost' && selectedOpp.status !== 'Disqualified' && (
                    <Button 
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-400 text-slate-900 w-full sm:w-auto"
                      onClick={() => handleConvertToProposal(selectedOpp)}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Convert to Proposal
                    </Button>
                  )}
                </div>

                {/* Audit Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs text-slate-400 pt-2 border-t">
                  {selectedOpp.created_by && <span>Created by: {selectedOpp.created_by}</span>}
                  {selectedOpp.created_at && <span className="hidden sm:inline">•</span>}
                  {selectedOpp.created_at && <span>{format(new Date(selectedOpp.created_at), 'MMM d, yyyy h:mm a')}</span>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          setShowNewClientForm(false);
          setShowNewContactForm(false);
          setShowContactDropdown(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">
              {selectedOpp ? 'Edit Opportunity' : 'New Opportunity'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 sm:space-y-6 pt-4">
            {/* Contact Selection - Primary way to select who this is for */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Contact Person
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewContactForm(!showNewContactForm)}
                  className="text-amber-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Contact
                </Button>
              </div>
              
              {/* Contact Search */}
              {!showNewContactForm && (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={contactSearch}
                        onChange={(e) => {
                          setContactSearch(e.target.value);
                          setShowContactDropdown(true);
                        }}
                        onFocus={() => setShowContactDropdown(true)}
                        placeholder="Search contacts by name, company, phone, email..."
                        className="h-10 pl-10 bg-slate-50"
                        data-testid="contact-search-input"
                      />
                      {formData.contact_id && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => {
                            setContactSearch('');
                            setFormData(prev => ({
                              ...prev,
                              contact_id: '',
                              contact_first_name: '',
                              contact_last_name: '',
                              contact_name: '',
                              contact_phone: '',
                              contact_email: '',
                              client_id: ''
                            }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Dropdown Results */}
                    {showContactDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {loadingPeople ? (
                        <div className="px-3 py-4 text-center text-slate-500">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                          <p className="text-sm">Loading contacts...</p>
                        </div>
                      ) : filteredPeople.length > 0 ? (
                        filteredPeople.map(person => (
                          <div
                            key={person.id}
                            className="px-3 py-2 hover:bg-amber-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => handleSelectPerson(person)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-slate-900">{person.full_name}</span>
                              {person.job_title && (
                                <span className="text-xs text-slate-500">{person.job_title}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              {person.company_name && (
                                <span className="flex items-center gap-1">
                                  <Building className="w-3 h-3" />
                                  {person.company_name}
                                </span>
                              )}
                              {person.primary_phone && (
                                <span>{person.primary_phone}</span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-slate-500">
                          <p>No contacts found</p>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setShowContactDropdown(false);
                              setShowNewContactForm(true);
                            }}
                            className="text-amber-600"
                          >
                            + Create new contact
                          </Button>
                        </div>
                      )}
                      <div 
                        className="px-3 py-2 bg-slate-50 text-center border-t cursor-pointer hover:bg-slate-100"
                        onClick={() => setShowContactDropdown(false)}
                      >
                        <span className="text-sm text-slate-500">Close</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Selected Contact - Editable Fields */}
                {formData.contact_id && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-800">Selected Contact</span>
                      <Badge variant="outline" className="text-green-600 border-green-300">Linked</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-green-700">First Name *</Label>
                        <Input
                          value={formData.contact_first_name}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            contact_first_name: e.target.value,
                            contact_name: `${e.target.value} ${prev.contact_last_name}`.trim()
                          }))}
                          className="h-10 bg-white border-green-300"
                          placeholder="First name"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-green-700">Last Name *</Label>
                        <Input
                          value={formData.contact_last_name}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            contact_last_name: e.target.value,
                            contact_name: `${prev.contact_first_name} ${e.target.value}`.trim()
                          }))}
                          className="h-10 bg-white border-green-300"
                          placeholder="Last name"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-green-700">Phone</Label>
                        <Input
                          value={formData.contact_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                          className="h-10 bg-white border-green-300"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-green-700">Email</Label>
                        <Input
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                          className="h-10 bg-white border-green-300"
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-600">These details will transfer to Proposal and Job</p>
                  </div>
                )}
              </div>
              )}

              {/* No Contact Selected - Show Manual Entry Fields */}
              {!formData.contact_id && !showNewContactForm && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
                  <p className="text-sm text-slate-600">Or enter contact details manually:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">First Name *</Label>
                      <Input
                        value={formData.contact_first_name}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact_first_name: e.target.value,
                          contact_name: `${e.target.value} ${prev.contact_last_name}`.trim()
                        }))}
                        className="h-10 bg-white"
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Last Name *</Label>
                      <Input
                        value={formData.contact_last_name}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          contact_last_name: e.target.value,
                          contact_name: `${prev.contact_first_name} ${e.target.value}`.trim()
                        }))}
                        className="h-10 bg-white"
                        placeholder="Last name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Phone</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                        className="h-10 bg-white"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                        className="h-10 bg-white"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Inline New Contact Form */}
              {showNewContactForm && (
                <Card className="border-amber-300 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Create New Contact
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewContactForm(false)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">First Name *</Label>
                        <Input
                          value={newContactData.first_name}
                          onChange={(e) => setNewContactData(prev => ({ ...prev, first_name: e.target.value }))}
                          placeholder="John"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium">Last Name *</Label>
                        <Input
                          value={newContactData.last_name}
                          onChange={(e) => setNewContactData(prev => ({ ...prev, last_name: e.target.value }))}
                          placeholder="Smith"
                          className="bg-white"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Company (Optional)</Label>
                        <div className="space-y-2">
                          <select
                            value={newContactData.company_id}
                            onChange={(e) => {
                              if (e.target.value === '__new__') {
                                setNewContactData(prev => ({
                                  ...prev,
                                  company_id: '__new__',
                                  company_name: ''
                                }));
                              } else {
                                const company = clients.find(c => c.id === e.target.value);
                                setNewContactData(prev => ({
                                  ...prev,
                                  company_id: e.target.value,
                                  company_name: company?.company_name || company?.client_name || ''
                                }));
                              }
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                          >
                            <option value="">No Company (Individual)</option>
                            <option value="__new__" className="text-amber-600">+ Create New Company</option>
                            {clients.map(c => (
                              <option key={c.id} value={c.id}>{c.company_name || c.client_name}</option>
                            ))}
                          </select>
                          {newContactData.company_id === '__new__' && (
                            <Input
                              value={newContactData.company_name}
                              onChange={(e) => setNewContactData(prev => ({ ...prev, company_name: e.target.value }))}
                              placeholder="Enter new company name..."
                              className="bg-white border-amber-300"
                              autoFocus
                            />
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Job Title</Label>
                        <Input
                          value={newContactData.job_title}
                          onChange={(e) => setNewContactData(prev => ({ ...prev, job_title: e.target.value }))}
                          placeholder="Project Manager"
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Phone</Label>
                        <Input
                          type="tel"
                          value={newContactData.phone}
                          onChange={(e) => setNewContactData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="(704) 555-1234"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={newContactData.email}
                          onChange={(e) => setNewContactData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@company.com"
                          className="bg-white"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewContactForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        onClick={handleCreateContact}
                        disabled={savingContact || !newContactData.first_name.trim() || !newContactData.last_name.trim()}
                      >
                        {savingContact ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="w-4 h-4 mr-1" />
                        )}
                        Create & Select
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Company Selection (Secondary - auto-filled from contact) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Company / Client
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewClientForm(!showNewClientForm)}
                  className="text-amber-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Company
                </Button>
              </div>
              
              {!showNewClientForm && (
                <div className="space-y-2">
                  <Label>Company</Label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowNewClientForm(true);
                        setFormData(prev => ({ ...prev, client_id: '' }));
                      } else {
                        setShowNewClientForm(false);
                        setFormData(prev => ({ ...prev, client_id: e.target.value }));
                      }
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm"
                    data-testid="client-select"
                  >
                    <option value="">No company selected</option>
                    <option value="__new__" className="text-amber-600 font-medium">+ Add New Company</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name || c.client_name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">Auto-filled from contact selection, or select manually</p>
                </div>
              )}

              {/* Inline New Client Form */}
              {showNewClientForm && (
                <Card className="border-amber-300 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Create New Client
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewClientForm(false)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">This client will be added to your CRM</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Company Info */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Company/Client Name *</Label>
                      <Input
                        value={newClientData.client_name}
                        onChange={(e) => setNewClientData(prev => ({ ...prev, client_name: e.target.value }))}
                        placeholder="Company or individual name"
                        className="bg-white"
                      />
                    </div>

                    {/* Primary Contact */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Contact Name</Label>
                        <Input
                          value={newClientData.contact_name}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, contact_name: e.target.value }))}
                          placeholder="John Smith"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={newClientData.contact_email}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, contact_email: e.target.value }))}
                          placeholder="email@company.com"
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Phone</Label>
                        <Input
                          type="tel"
                          value={newClientData.contact_phone}
                          onChange={(e) => setNewClientData(prev => ({ ...prev, contact_phone: e.target.value }))}
                          placeholder="(555) 123-4567"
                          className="bg-white"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-3">
                      <Label className="text-xs font-medium">Address (Optional - will auto-fill Site Address)</Label>
                      <Input
                        value={newClientData.physical_street}
                        onChange={(e) => setNewClientData(prev => ({ ...prev, physical_street: e.target.value }))}
                        placeholder="Street address"
                        className="h-10 bg-white"
                      />
                      <div className="grid grid-cols-6 gap-2">
                        <div className="col-span-3">
                          <Input
                            value={newClientData.physical_city}
                            onChange={(e) => setNewClientData(prev => ({ ...prev, physical_city: e.target.value }))}
                            placeholder="City"
                            className="h-10 bg-white"
                          />
                        </div>
                        <div className="col-span-2">
                          <select
                            value={newClientData.physical_state}
                            onChange={(e) => setNewClientData(prev => ({ ...prev, physical_state: e.target.value }))}
                            className="flex h-10 w-full rounded-md border border-input bg-white px-2 py-2 text-sm"
                          >
                            <option value="">State</option>
                            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="col-span-1">
                          <Input
                            value={newClientData.physical_zip}
                            onChange={(e) => setNewClientData(prev => ({ ...prev, physical_zip: e.target.value }))}
                            placeholder="ZIP"
                            className="h-10 bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewClientForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                        onClick={handleCreateClient}
                        disabled={savingClient || !newClientData.client_name.trim()}
                      >
                        {savingClient ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="w-4 h-4 mr-1" />
                        )}
                        Create & Select Client
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Title & Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., 123 Main St - Gas Line Survey"
                  className="h-10 bg-slate-50"
                />
                <p className="text-xs text-slate-500">Where is the job? This will flow through to Proposal and Job.</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm"
                  data-testid="opportunity-status-select"
                >
                  {OPPORTUNITY_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Service Line Items */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />Service Line Items
                </h3>
                {(formData.service_lines || []).length > 1 && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <GripVertical className="w-3 h-3" /> Drag to reorder
                  </p>
                )}
              </div>
              
              {(formData.service_lines || []).length === 0 ? (
                <div className="text-center py-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <p className="text-sm text-slate-500">No service lines yet. Add line items to estimate the job.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      service_lines: [...(prev.service_lines || []), { ...emptyServiceLine, id: Date.now() }]
                    }))}
                  >
                    <Plus className="w-4 h-4 mr-1" />Add Service Line
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <SortableServiceLines
                    serviceLines={formData.service_lines}
                    services={services}
                    onServiceLinesChange={(newLines) => setFormData(prev => ({ ...prev, service_lines: newLines }))}
                    onShowQuantityDialog={(data) => {
                      setQuantityDialogData(data);
                      setShowQuantityDialog(true);
                    }}
                    emptyServiceLine={emptyServiceLine}
                  />
                  
                  {/* Total */}
                  <div className="flex justify-end p-3 bg-green-100 rounded-lg">
                    <div className="text-right">
                      <span className="text-sm text-green-700">Estimated Total: </span>
                      <span className="text-lg font-bold text-green-800">
                        ${(formData.service_lines || []).reduce((sum, l) => sum + (l.total || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Add Service Line Button - at bottom */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      service_lines: [...(prev.service_lines || []), { ...emptyServiceLine, id: Date.now() }]
                    }))}
                  >
                    <Plus className="w-4 h-4 mr-1" />Add Service Line
                  </Button>
                </div>
              )}
            </div>

            {/* Site Address */}
            <div className="space-y-4">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />📍 Job Site Address
                </h3>
                <p className="text-xs text-green-600 mt-1">Where will the work be performed? This can be different from the client's office or billing address.</p>
              </div>
              <div className="space-y-3">
                <Input
                  value={formData.site_address_street}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_address_street: e.target.value }))}
                  placeholder="Street address or site location description"
                  className="h-10 bg-slate-50"
                />
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3">
                    <Input
                      value={formData.site_address_city}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_address_city: e.target.value }))}
                      placeholder="City"
                      className="h-10 bg-slate-50"
                    />
                  </div>
                  <div className="col-span-2">
                    <select
                      value={formData.site_address_state}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_address_state: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm"
                    >
                      <option value="">State</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <Input
                      value={formData.site_address_zip}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_address_zip: e.target.value }))}
                      placeholder="ZIP"
                      className="h-10 bg-slate-50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Site Plan Notes */}
            <div className="space-y-2">
              <Label>Site Plan / Request Notes</Label>
              <Textarea
                value={formData.site_plan_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, site_plan_notes: e.target.value }))}
                rows={3}
                placeholder="Describe the job site, access requirements, scope of work..."
                className="bg-slate-50"
              />
            </div>

            {/* Expected Close & Notes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expected Close Date</Label>
                <Input
                  type="date"
                  value={formData.expected_close_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_close_date: e.target.value }))}
                  className="h-10 bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes..."
                  className="h-10 bg-slate-50"
                />
              </div>
            </div>

            {/* File Attachments Section */}
            {selectedOpp ? (
              <FileUpload
                files={selectedOpp.attachments || []}
                uploadEndpoint={`/api/opportunities/${selectedOpp.id}/files`}
                deleteEndpoint={`/api/opportunities/${selectedOpp.id}/files`}
                onUploadSuccess={async () => {
                  // Refresh the opportunity to get updated attachments
                  try {
                    const response = await axios.get(`${API}/opportunities/${selectedOpp.id}`);
                    setSelectedOpp(response.data);
                    setFormData(prev => ({ ...prev, attachments: response.data.attachments || [] }));
                  } catch (error) {
                    console.error('Failed to refresh:', error);
                  }
                }}
                onDeleteSuccess={async () => {
                  try {
                    const response = await axios.get(`${API}/opportunities/${selectedOpp.id}`);
                    setSelectedOpp(response.data);
                    setFormData(prev => ({ ...prev, attachments: response.data.attachments || [] }));
                  } catch (error) {
                    console.error('Failed to refresh:', error);
                  }
                }}
                title="Attachments"
                emptyMessage="Drag & drop files here, or click to upload. Site plans, photos, documents."
              />
            ) : (
              <div className="p-4 bg-amber-50 border border-dashed border-amber-300 rounded-lg text-center">
                <Paperclip className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                <p className="text-sm text-amber-700 font-medium">File Attachments</p>
                <p className="text-xs text-amber-600 mt-1">
                  Click "Save & Add Files" below to save and enable file uploads.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              {!selectedOpp && (
                <Button 
                  onClick={() => handleSave(true)} 
                  disabled={saving} 
                  variant="outline"
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  <Paperclip className="w-4 h-4 mr-2" />
                  Save & Add Files
                </Button>
              )}
              <Button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                data-testid="opportunity-save-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedOpp ? 'Update' : 'Create'} Opportunity
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Opportunity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedOpp?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Service Quantity Dialog */}
      <Dialog open={showQuantityDialog} onOpenChange={setShowQuantityDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{quantityDialogData.quantityPrompt}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>
                {quantityDialogData.serviceName} - {quantityDialogData.quantityLabel}
              </Label>
              <Input
                type="number"
                min="1"
                value={quantityDialogData.quantity}
                onChange={(e) => setQuantityDialogData(prev => ({
                  ...prev,
                  quantity: parseInt(e.target.value) || 1
                }))}
                className="text-lg"
                autoFocus
                data-testid="quantity-input"
              />
              <p className="text-sm text-slate-500">
                Result: <span className="font-semibold text-blue-600">
                  {quantityDialogData.serviceName}: {quantityDialogData.quantity} {quantityDialogData.quantityLabel}
                </span>
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowQuantityDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                onClick={() => {
                  const serviceWithQuantity = `${quantityDialogData.serviceName}: ${quantityDialogData.quantity} ${quantityDialogData.quantityLabel}`;
                  setFormData(prev => ({
                    ...prev,
                    service_lines: prev.service_lines.map((l, i) => 
                      i === quantityDialogData.serviceIndex ? { 
                        ...l, 
                        service_type: serviceWithQuantity,
                        unit_price: quantityDialogData.price || l.unit_price,
                        total: (l.quantity || 1) * (quantityDialogData.price || l.unit_price)
                      } : l
                    )
                  }));
                  setShowQuantityDialog(false);
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
